import { Link } from "react-router-dom";
import { MEDIA, MINISTRIES } from "@/lib/church";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";

// Polaroid scrapbook — 4 still photos, slightly rotated, layered on a deep purple board
function PolaroidCollage({ images, alt }) {
    // hand-tuned positions: top%, left%, width%, rotate, z-index
    const layout = [
        { top: "6%",  left: "8%",  width: "44%", rotate: -7, z: 2 },
        { top: "10%", left: "52%", width: "40%", rotate:  6, z: 1 },
        { top: "50%", left: "3%",  width: "48%", rotate:  4, z: 5 },
        { top: "48%", left: "52%", width: "44%", rotate: -5, z: 3 },
    ];
    return (
        <div className="absolute inset-0 bg-[#2E1A47]" data-testid="volunteer-gallery">
            {/* subtle gold rays */}
            <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#FFC72C]/15 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -right-16 w-80 h-80 bg-[#FFC72C]/10 blur-3xl rounded-full" />
            {images.slice(0, 4).map((src, i) => {
                const p = layout[i] || layout[0];
                return (
                    <figure
                        key={src}
                        className="absolute bg-white p-2 pb-5 shadow-2xl"
                        style={{
                            top: p.top,
                            left: p.left,
                            width: p.width,
                            transform: `rotate(${p.rotate}deg)`,
                            zIndex: p.z,
                        }}
                    >
                        <img
                            src={src}
                            alt={alt}
                            className="block w-full aspect-square object-cover object-top"
                        />
                        {/* gold washi tape accent on the top one */}
                        {i === 0 && (
                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-3 bg-[#FFC72C]/80 rotate-3" />
                        )}
                    </figure>
                );
            })}
        </div>
    );
}

export default function Ministries() {
    return (
        <div data-testid="ministries-page" className="bg-white">
            {/* HERO */}
            <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#4C1D95]">
                <div className="absolute inset-0">
                    <img src={MEDIA.pews} alt="Ministries" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4C1D95]/95 via-[#4C1D95]/60 to-[#4C1D95]/20" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/80 via-transparent to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 w-full">
                    <p className="overline text-[#FFC72C] mb-4 animate-fade-up">Get Connected</p>
                    <h1 className="font-display text-6xl sm:text-7xl lg:text-7xl font-bold tracking-tight text-white leading-[0.85] max-w-5xl animate-fade-up animate-delay-100" data-testid="ministries-title">
                        Find your <span className="text-[#FFC72C] italic">people</span>.<br />
                        Find your purpose.
                    </h1>
                    <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up animate-delay-200">
                        Our ministries are where faith becomes friendship — and where service becomes a way of life.
                    </p>
                </div>
            </section>

            {/* MINISTRIES TETRIS GRID */}
            <section className="bg-white py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {MINISTRIES.map((m, i) => {
                            const isLarge = i === 0 || i === 3;
                            const isAccent = i % 3 === 1;
                            return (
                                <div
                                    key={m.id}
                                    data-testid={`ministry-page-${m.id}`}
                                    className={`group relative overflow-hidden ${isLarge ? 'md:col-span-2' : ''}`}
                                >
                                    <div className={`grid ${isLarge ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                                        <div className="relative aspect-[4/3] overflow-hidden zoom-on-hover" style={m.bgColor ? { backgroundColor: m.bgColor } : undefined}>
                                            {m.gallery ? (
                                                <PolaroidCollage images={m.gallery} alt={m.name} />
                                            ) : (
                                                <img
                                                    src={m.image}
                                                    alt={m.name}
                                                    className={`absolute inset-0 w-full h-full ${m.contain ? 'object-contain p-6' : 'object-cover'}`}
                                                    style={m.rotate ? { transform: `rotate(${m.rotate}deg) scale(1.5)`, transformOrigin: "center" } : undefined}
                                                />
                                            )}
                                            {!m.contain && !m.gallery && <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/40 to-transparent" />}
                                        </div>
                                        <div className={`p-8 lg:p-12 flex flex-col justify-center ${isAccent ? 'bg-[#FFC72C] text-[#2E1A47]' : 'bg-[#F4F7F9] text-[#2E1A47]'}`}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className={`w-10 h-10 ${isAccent ? 'bg-[#4C1D95] text-[#FFC72C]' : 'bg-[#4C1D95] text-[#FFC72C]'} flex items-center justify-center font-display text-lg font-black`}>{String(i + 1).padStart(2, "0")}</span>
                                                <p className={`overline ${isAccent ? 'text-[#2E1A47]' : 'text-[#4C1D95]'}`}>{m.tagline}</p>
                                            </div>
                                            <h3 className={`font-display ${isLarge ? 'text-4xl lg:text-5xl' : 'text-3xl lg:text-4xl'} font-bold tracking-tight leading-[0.9] mb-4`}>
                                                {m.id === "house2house" ? (
                                                    <>House<span className="text-[#FFC72C] font-black text-[1.3em] align-middle">²</span>House Ministries</>
                                                ) : m.id === "sheame" ? (
                                                    <><i>She</i>&nbsp;&nbsp;AME</>
                                                ) : m.name}
                                            </h3>
                                            <p className={`leading-relaxed ${isAccent ? 'text-[#2E1A47]/80' : 'text-slate-600'} mb-6`}>{m.description}</p>
                                            <Link to={m.id === "house2house" ? "/house2house" : "/contact"} className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest font-black group-hover:gap-3 transition-all w-fit ${isAccent ? 'text-[#2E1A47]' : 'text-[#4C1D95]'}`}>
                                                {m.id === "house2house" ? "Learn More" : "Get Involved"} <ArrowUpRight size={14} weight="bold" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#4C1D95] py-20 lg:py-28 text-white relative overflow-hidden">
                <div className="absolute -right-32 -top-32 w-[500px] h-[500px] bg-[#FFC72C]/15 blur-3xl rounded-full" />
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <p className="overline text-[#FFC72C] mb-4">Take the next step</p>
                    <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.85] mb-6">
                        Not sure where<br />
                        to <span className="text-[#FFC72C] italic">start?</span>
                    </h2>
                    <p className="text-lg lg:text-xl text-white/85 mb-10 leading-relaxed">Reach out and let us help you find a ministry that fits your gifts, your season, and your story.</p>
                    <Link to="/contact" data-testid="ministries-contact-btn" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-12 py-5 text-sm font-black uppercase tracking-widest transition-colors">
                        Connect With Us <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
